import { useRef } from 'react'
import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { X, SearchIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { DataTableFacetedFilter } from '@/views/users/components/data-table-faceted-filter'
import { DataTableViewOptions } from '@/components/data-table/data-table-view-options'
import { debounceAsync } from '@/utils/debounce'
import type { DocumentsTableParams } from '../index'

interface DocumentsToolbarProps<TData> {
  table: Table<TData>
  params: DocumentsTableParams
  setParams: React.Dispatch<React.SetStateAction<DocumentsTableParams>>
}

const FILE_TYPE_OPTIONS = [
  { label: 'PDF', value: 'application/pdf' },
  { label: 'Word', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  { label: 'Excel', value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  { label: 'Image', value: 'image/' },
]

export function DocumentsToolbar<TData>({
  table,
  params,
  setParams,
}: DocumentsToolbarProps<TData>) {
  const { t } = useTranslation()

  const debouncedSearch = useRef(
    debounceAsync(async (value: string) => {
      setParams(p => ({ ...p, search: value === '' ? null : value, page: 0 }))
    }, 400),
  ).current

  const hasFilters = !!params.search || !!params.content_type

  const handleTypeChange = (values: string[]) => {
    setParams(p => ({ ...p, content_type: values.length > 0 ? values[0] : null, page: 0 }))
  }

  const toggleFullText = (checked: boolean) => {
    setParams(p => ({
      ...p,
      fullText: checked,
      content_type: null, // clear type filter — not relevant for full-text search
      page: 0,
    }))
  }

  const resetAll = () => {
    setParams(p => ({
      ...p,
      search: null,
      content_type: null,
      fullText: false,
      page: 0,
    }))
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Full-text search toggle banner */}
      <div className={`flex items-center gap-3 rounded-md border px-3 py-2 transition-colors ${params.fullText ? 'border-primary/40 bg-primary/5' : 'border-transparent'}`}>
        <Switch
          id="full-text-toggle"
          checked={params.fullText}
          onCheckedChange={toggleFullText}
        />
        <Label htmlFor="full-text-toggle" className="cursor-pointer flex items-center gap-2 text-sm">
          <SearchIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-medium">{t('full_text_search')}</span>
          <span className="text-muted-foreground hidden sm:inline">— {t('full_text_search_desc')}</span>
        </Label>
      </div>

      {/* Filters row */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder={params.fullText ? t('full_text_search_placeholder') : t('doc_search_placeholder')}
            defaultValue={params.search ?? ''}
            key={String(params.fullText)} // re-mount to clear value when toggling mode
            onChange={(e) => debouncedSearch(e.target.value)}
            className={`h-9 w-full sm:max-w-[320px] ${params.fullText ? 'border-primary/60 focus-visible:ring-primary/40' : ''}`}
          />

          {/* Hide type filter in full-text mode */}
          {!params.fullText && (
            <DataTableFacetedFilter
              title={t('doc_col_type')}
              options={FILE_TYPE_OPTIONS}
              defaultSelected={params.content_type ? [params.content_type] : []}
              onFacetChange={handleTypeChange}
            />
          )}

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={resetAll} className="h-9 px-2 text-muted-foreground hover:text-foreground">
              {t('reset_filters') || 'Réinitialiser'}
              <X className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>

        <DataTableViewOptions table={table} />
      </div>
    </div>
  )
}

